import { useCallback, useEffect, useState } from "react";

export type ThemePreference = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";

const STORAGE_KEY = "vd-theme";

function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

/** Applies a theme to <html data-theme>. System (follow the OS) is the default. */
export function applyTheme(pref: ThemePreference): ResolvedTheme {
  const resolved = pref === "system" ? systemTheme() : pref;
  if (typeof document !== "undefined") document.documentElement.dataset.theme = resolved;
  return resolved;
}

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "dark" || v === "light" || v === "system") return v;
  } catch {
    // storage unavailable: fall through to the default
  }
  return "system";
}

/** Theme preference hook. Persists per device; follows the OS live when set to system. */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readStored);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => applyTheme(preference));

  useEffect(() => {
    setResolved(applyTheme(preference));
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // ignore
    }
    if (preference !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setResolved(applyTheme("system"));
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  const setTheme = useCallback((p: ThemePreference) => setPreference(p), []);
  return { preference, resolved, setTheme };
}
